import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import CourseForm, { ICourseFormData } from '../../components/forms/CourseForm';
import ActivityPicker from '../../components/forms/ActivityPicker';
import { translateError } from '../../utils/errorMessages';

const CourseCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activityId, setActivityId] = useState<number | null>(null);
  const [activityError, setActivityError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-select an activity when arriving via ?activityId=<n>.
  useEffect(() => {
    const param = searchParams.get('activityId');
    if (param) {
      const parsed = Number(param);
      if (Number.isFinite(parsed)) setActivityId(parsed);
    }
  }, [searchParams]);

  const submitHandler = async (data: ICourseFormData) => {
    if (activityId === null) {
      setActivityError(t('activity.picker.requiredError'));
      return;
    }
    setActivityError(undefined);
    setLoading(true);
    setError(null);

    try {
      await api.courses.create({
        activityId,
        name: data.name,
        description: data.description,
        capacity: data.capacity === '' || data.capacity == null ? null : Number(data.capacity),
        enrollmentPolicy: data.enrollmentPolicy,
        isActive: data.isActive,
        pricePerSession: data.pricePerSession === '' || data.pricePerSession == null ? null : Number(data.pricePerSession),
        instructorId: data.instructorId,
        termStart: data.termStart === '' ? null : data.termStart,
        termEnd: data.termEnd === '' ? null : data.termEnd,
      });
      setLoading(false);
      addToast(t('courses.created'), 'success');
      navigate('/admin/courses');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
    }
  };

  return (
    <div className="admin-form-page">
      <h2>{t('courses.create')}</h2>
      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <div className="form-container" style={{ marginBottom: '1rem' }}>
        <ActivityPicker
          value={activityId}
          onChange={(v) => { setActivityId(v); if (v !== null) setActivityError(undefined); }}
          required
          label={t('courses.activity')}
          error={activityError}
        />
      </div>

      <CourseForm
        mode="create"
        onSubmit={submitHandler}
        loading={loading}
        submitLabel={t('courses.create')}
      />

      <Link to="/admin/courses" className="form-link">{t('common.back')}</Link>
    </div>
  );
};

export default CourseCreatePage;
